/**
 * Access (screenshots/52, brief §2.9; the handoff starter's settings/Access.tsx, built out): owner/admin only (feature `access`).
 * Since Oct 16 it is "Members & roles" in Team settings (/dashboard/settings/access, TeamSettingsPage.tsx TeamAccessPage,
 * `embedded`): the same screen inside the settings layout, no rail item.
 * Role changes follow accessRules.ts (admins can't touch owners or make owners; the last owner can't be demoted),
 * enforced again by the Worker. Equal role cards, the members
 * table (60px rows, role select, pending invite chip), account type, sign-in rules, flags to review.
 * Backed by /api/dashboard/access (Better Auth organization plugin) + access/role and access/rules
 * (worker/src/dashboard-account.js); paints from snapshot:access.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowRight, ChevronDown, Lock, MoreHorizontal, UserPlus } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { Switch } from "../components/shared/ui";
import { GROUPS, type OrgRole } from "../nav/navConfig";
import { toastStore } from "../stores/toastStore";
import { initialsOf, inviteSentLabel, lastActiveLabel } from "../account/logic";
import { canChangeRole, roleCounts, type Member } from "./accessRules";
import type { AccessSnapshot } from "../account/types";
import { REASONS } from "../people/LeaningCard"; // leaning-flag reasons, as worded on the flag menu (24)
import { useBusy } from "../hooks/useBusy";
import { useDismiss } from "../hooks/useDismiss";
import { errorText } from "../lib/apiError";
import { SeatLimitCard } from "../plans/LimitCards";
import { limitAnswer, seatsFull, type SeatInfo } from "../plans/planLimits";

const ROLE_CARDS: { role: OrgRole; title: string; text: string }[] = [
  { role: "owner", title: "Owner", text: "Everything, including billing and deleting the account" },
  { role: "admin", title: "Admin", text: "Invite and remove people, set team positions, publish team views, review flags" },
  { role: "member", title: "Member", text: "Use every tool: follows, notes, target lists, briefs, exports" },
];
const ROLE_LABEL: Record<OrgRole, string> = { owner: "Owner", admin: "Admin", member: "Member" };

async function call(path: string, method: string, body?: unknown) {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
  const data = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) toastStore.getState().show({ message: errorText(data) ?? "Couldn't save that. Try again." });
  return res?.ok ? data : null;
}

/** Role select: options the actor may not pick are disabled; with none left it's the locked chip ("Owner 🔒"). */
function RoleSelect({ role, allowed, disabled, label, onChange }: { role: OrgRole; allowed: OrgRole[]; disabled?: boolean; label: string; onChange: (r: OrgRole) => void }) {
  if (!allowed.some((r) => r !== role)) return <span className="role-select role-select--locked">{ROLE_LABEL[role]}<Lock size={14} /></span>;
  return (
    <Dropdown className="role-select" label={`Role for ${label}`} value={role} disabled={disabled} onChange={onChange} align="end" chevron={<ChevronDown size={14} aria-hidden />}
      options={(["owner", "admin", "member"] as OrgRole[]).map((r) => ({ value: r, label: ROLE_LABEL[r], disabled: !allowed.includes(r) }))} />
  );
}

function RowMenu({ label, action, onAction }: { label: string; action: string; onAction: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useDismiss(ref, open, () => setOpen(false));
  return (
    <span className="row-menu" ref={ref}>
      <button className="icon-btn" aria-label={`More for ${label}`} aria-expanded={open} onClick={() => setOpen((v) => !v)}><MoreHorizontal size={18} /></button>
      {open && <span className="row-menu__pop" role="menu"><button role="menuitem" onClick={() => { setOpen(false); onAction(); }}>{action}</button></span>}
    </span>
  );
}

export function Access({ initial, embedded = false }: { initial: AccessSnapshot; embedded?: boolean }) {
  const [data, setData] = useState(initial);
  const [inviting, setInviting] = useState(false);
  const [showFlags, setShowFlags] = useState(false);
  // Flags to review (backend BE1): resolve/dismiss a flag; platform owners/admins also confirm or reject "This is me" claims.
  const decideFlag = async (id: string, decision: "resolve" | "dismiss") => {
    if (!(await call(`/api/dashboard/access/flags/${encodeURIComponent(id)}`, "POST", { decision }))) return;
    setData((d) => ({ ...d, flags: { open: Math.max(0, d.flags.open - 1), items: d.flags.items.filter((f) => f.id !== id) } }));
  };
  const decideClaim = async (userId: string, personId: string, decision: "confirm" | "reject") => {
    if (!(await call(`/api/dashboard/admin/claims/${encodeURIComponent(userId)}/${encodeURIComponent(personId)}`, "POST", { decision }))) return;
    setData((d) => ({ ...d, claims: (d.claims ?? []).filter((c) => !(c.userId === userId && c.personId === personId)) }));
  };
  const asMembers: Member[] = [
    ...data.members.map((m) => ({ id: m.id, name: m.name, email: m.email, role: m.role, lastActive: m.lastActive })),
    ...data.invitations.map((i) => ({ id: i.id, name: i.name ?? i.email, email: i.email, role: i.role, lastActive: null, pending: true })),
  ];
  const counts = roleCounts(asMembers);
  const allowed = (m: Member) => (["owner", "admin", "member"] as OrgRole[]).filter((r) => r === m.role || canChangeRole(data.me.role, m, r, asMembers).ok);
  const now = new Date();
  const reload = async () => { const d = await call("/api/dashboard/access", "GET"); if (d) setData(d as AccessSnapshot); };
  useEffect(() => { document.title = `${embedded ? "Members & roles" : "Access"} · Chicago Politics`; }, [embedded]);

  const setRole = async (memberId: string, role: OrgRole) => {
    const prev = data;
    setData({ ...data, members: data.members.map((m) => (m.id === memberId ? { ...m, role } : m)) });
    if (!(await call("/api/dashboard/access/role", "POST", { memberId, role }))) setData(prev);
  };
  const setRules = async (patch: Partial<AccessSnapshot["rules"]>) => {
    const prev = data;
    setData({ ...data, rules: { ...data.rules, ...patch } });
    const saved = await call("/api/dashboard/access/rules", "PUT", patch);
    if (!saved) setData(prev);
  };
  const people = data.members.length + data.invitations.length;

  return (
    <div className="access-page" data-embedded={embedded || undefined}>
      <header className="access-head">
        <div>
          {embedded ? <h2 className="access-title">Members & roles</h2> : <h1 className="page-title">Access</h1>}
          <p className="access-sub">{data.organization.name} · {GROUPS[data.organization.group].label} account · {people} member{people === 1 ? "" : "s"}</p>
        </div>
        <div className="access-invite">
          <button className="btn btn--primary btn--lg" aria-expanded={inviting} onClick={() => setInviting((v) => !v)}><UserPlus size={16} />Invite people</button>
          {inviting && <InviteForm orgName={data.organization.name} seats={data.seats ?? null} onDone={async () => { setInviting(false); await reload(); }}
            onManage={() => { setInviting(false); document.querySelector(".members")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }); }} />}
        </div>
      </header>

      <div className="role-cards">
        {ROLE_CARDS.map((c) => (
          <div key={c.role} className="role-card">
            <h2>{c.title}<span className="role-card__count">{counts[c.role]}</span></h2>
            <p>{c.text}</p>
          </div>
        ))}
      </div>

      <List as="div" className="members members--access" role="table" aria-label="Members">
        <ListRow as="div" className="members__head ls-head" role="row">
          <RowMain min={160}><span role="columnheader">Person</span></RowMain>
          <RowCell width={162}><span role="columnheader">Role</span></RowCell>
          <RowCell width={160}><span role="columnheader">Last active</span></RowCell>
          <RowCell width={30} />
        </ListRow>
        {data.members.map((m) => (
          <ListRow key={m.id} as="div" className="members__row" role="row">
            <RowLead><span className="members__avatar" aria-hidden>{initialsOf(m.name || m.email)}</span></RowLead>
            <RowMain min={160}><span className="members__name" role="cell"><RowTitle as="b">{m.name || m.email}</RowTitle><RowSub>{m.email}</RowSub></span></RowMain>
            <RowCell width={162}><span role="cell"><RoleSelect role={m.role} label={m.name || m.email} allowed={allowed(asMembers.find((x) => x.id === m.id)!)} disabled={m.id === data.me.memberId} onChange={(r) => void setRole(m.id, r)} /></span></RowCell>
            <RowCell width={160}><span role="cell" className="members__active">{lastActiveLabel(m.lastActive, now)}</span></RowCell>
            <RowCell width={30} align="end">
              <span role="cell">
                {m.role !== "owner" && m.id !== data.me.memberId && (
                  <RowMenu label={m.name || m.email} action="Remove" onAction={async () => { if (await call("/api/dashboard/access/remove", "POST", { memberId: m.id })) await reload(); }} />
                )}
              </span>
            </RowCell>
          </ListRow>
        ))}
        {data.invitations.map((i) => (
          <ListRow key={i.id} as="div" className="members__row" role="row">
            <RowLead><span className="members__avatar" aria-hidden>{initialsOf(i.name || i.email.split("@")[0]!.replace(/[._-]+/g, " "))}</span></RowLead>
            <RowMain min={160}><span className="members__name" role="cell"><RowTitle as="b">{i.name || i.email.split("@")[0]}</RowTitle><RowSub>{i.email}</RowSub></span></RowMain>
            <RowCell width={162}><span role="cell"><RoleSelect role={i.role} label={i.email} allowed={["admin", "member"]} disabled onChange={() => {}} /></span></RowCell>
            <RowCell width={160}><span role="cell"><span className="chip chip--warning">{inviteSentLabel(i.sent)}</span></span></RowCell>
            <RowCell width={30} align="end">
              <span role="cell">
                <RowMenu label={i.email} action="Cancel invite" onAction={async () => { if (await call("/api/dashboard/access/cancel", "POST", { invitationId: i.id })) await reload(); }} />
              </span>
            </RowCell>
          </ListRow>
        ))}
      </List>

      <div className="access-cards">
        <section className="access-card">
          <h2>Account type</h2>
          <div className="row-gap access-type"><span className="chip chip--positive">Verified organization</span><b>{GROUPS[data.organization.group].label}</b></div>
          <p>Sets which tabs and tools your team gets. Changing it needs re-verification.</p>
        </section>
        <section className="access-card">
          <h2>Sign-in rules</h2>
          {data.rules.joinDomain && (
            <div className="access-rule">
              <span>Anyone with @{data.rules.joinDomain} can request to join</span>
              <Switch on={data.rules.domainRequests} onChange={(v) => void setRules({ domainRequests: v })} label={`Anyone with @${data.rules.joinDomain} can request to join`} />
            </div>
          )}
          <div className="access-rule">
            <span>Require two-factor sign-in</span>
            <Switch on={data.rules.requireTwoFactor} onChange={(v) => void setRules({ requireTwoFactor: v })} label="Require two-factor sign-in" />
          </div>
        </section>
        <section className="access-card">
          <h2>Flags to review</h2>
          <div className="flags-count"><b>{data.flags.open}</b><span>forecasts or estimates your team flagged as inaccurate</span></div>
          {data.flags.open + (data.claims?.length ?? 0) > 0 && <button className="link-arrow" aria-expanded={showFlags} onClick={() => setShowFlags((v) => !v)}>Review flags<ArrowRight size={14} /></button>}
          {showFlags && (
            <ul className="flags-list">
              {data.flags.items.map((f) => (
                <li key={f.id}>
                  <b>{f.target}</b>{f.orgName && <small>{f.orgName}</small>}{f.reason && <small>{REASONS.find(([id]) => id === f.reason)?.[1]}</small>}{f.note && <small>{f.note}</small>}
                  {f.decidable !== false && <span className="flags-list__actions">
                    <button className="btn btn--sm" onClick={() => decideFlag(f.id, "resolve")}>Resolve</button>
                    <button className="btn btn--sm btn--ghost" onClick={() => decideFlag(f.id, "dismiss")}>Dismiss</button>
                  </span>}
                </li>
              ))}
              {(data.claims ?? []).map((c) => (
                <li key={`${c.userId}:${c.personId}`}>
                  <b>{c.name || c.email}</b><small>{c.email} · {c.personId}</small>
                  <span className="flags-list__actions">
                    <button className="btn btn--sm" onClick={() => decideClaim(c.userId, c.personId, "confirm")}>Confirm</button>
                    <button className="btn btn--sm btn--ghost" onClick={() => decideClaim(c.userId, c.personId, "reject")}>Reject</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Invite people (not designed beyond the button): email + role, then POST /api/dashboard/access/invite. When every
 * seat is in use (the plan's seat limit, design k9qobd #3) it is the seat limit card instead.
 */
function InviteForm({ orgName, seats: initialSeats, onDone, onManage }: { orgName: string; seats: SeatInfo | null; onDone: () => Promise<void>; onManage: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("member");
  const [seats, setSeats] = useState(initialSeats);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const { busy, run } = useBusy();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await fetch("/api/dashboard/access/invite", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, role }) }).catch(() => null);
      const body = res ? await res.json().catch(() => null) : null;
      const limit = limitAnswer(body);
      if (limit?.code === "seat_limit") { setSeats(limit.seats); return; }
      if (!res?.ok) { toastStore.getState().show({ message: errorText(body) ?? "Couldn't save that. Try again." }); return; }
      await onDone();
    });
  };
  if (seatsFull(seats)) return <SeatLimitCard seats={seats} orgName={orgName} email={email} onEmail={setEmail} onManage={onManage} />;
  return (
    <form className="invite-form" onSubmit={submit}>
      <input ref={input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
      <Dropdown className="role-select" label="Role" value={role} onChange={setRole} chevron={<ChevronDown size={14} aria-hidden />}
        options={[{ value: "member" as OrgRole, label: "Member" }, { value: "admin" as OrgRole, label: "Admin" }]} />
      <button className="btn btn--primary" type="submit" disabled={busy} aria-busy={busy || undefined}><UserPlus size={14} />Invite people</button>
    </form>
  );
}
